import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { auth, db } from '../../config/firebase';
import { useAPI } from '../../hooks/useAPI';
import { Person, Save, Check } from '@mui/icons-material';
import MyApartments from '../../components/profile/MyApartments';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import DeleteApartmentModal from '../../components/modals/DeleteApartmentModal';

const HomeV3 = () => {
  const [userInfo, setUserInfo] = useState(null);
  const [profileValues, setProfileValues] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    personalNumber: ''
  });
  const [myApartments, setMyApartments] = useState([]);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isChanged, setIsChanged] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressForm, setAddressForm] = useState({
    street: '',
    zipCode: '',
    city: '',
    apartmentNumber: '',
    entrance: ''
  });
  const [pendingPersonData, setPendingPersonData] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [apartmentToDelete, setApartmentToDelete] = useState(null);

  const { getUserInfo, getHousingInfo, saveUserInfo, processPersonDataAddress, saveAddressData, deleteHousing, isLoading } = useAPI();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userRef = doc(db, 'users', user.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            
            const newUserInfo = {
              uid: user.uid,
              email: userData.email || user.email || '',
              firstName: userData.firstName || '',
              lastName: userData.lastName || '',
              fullName: userData.fullName || '',
              personalNumber: userData.personalNumber || '',
              role: userData.role || 'user'
            };
            
            // Sätt userInfo från Firestore-data
            setUserInfo(newUserInfo);
            
            // Initialisera profileValues från userInfo (endast första gången)
            setProfileValues({
              firstName: newUserInfo.firstName || '',
              lastName: newUserInfo.lastName || '',
              email: newUserInfo.email || '',
              phoneNumber: userData.phoneNumber || '',
              personalNumber: newUserInfo.personalNumber || ''
            });
            
            // Redirect admin-användare till rätt dashboard
            if (userData.role === 'brfadmin') {
              navigate('/dashboard');
              return;
            } else if (userData.role === 'hyria_admin') {
              navigate('/hyria-dashboard');
              return;
            }
          } else {
            // Om ingen Firestore-data finns, skapa fallback userInfo
            const fallbackUserInfo = {
              uid: user.uid,
              email: user.email || '',
              firstName: '',
              lastName: '',
              fullName: '',
              personalNumber: '',
              role: 'user'
            };
            setUserInfo(fallbackUserInfo);
            
            // Initialisera profileValues för fallback också
            setProfileValues({
              firstName: '',
              lastName: '',
              email: user.email || '',
              phoneNumber: '',
              personalNumber: ''
            });
          }
        } catch (error) {
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user?.uid) return;
      
      // Load user info
      const info = await getUserInfo(user.uid);
      if (info) {
        setUserInfo(info);
        setProfileValues({
          firstName: info.firstName || '',
          lastName: info.lastName || '',
          email: info.email || '',
          phoneNumber: info.phoneNumber || '',
          personalNumber: info.personalNumber || ''
        });
      }

      // Load housing info (keeping for backward compatibility)
      const housing = await getHousingInfo(user.uid);
      if (housing) {
        // Convert old housing format to new apartment format for initial data
        const convertedApartments = housing.map(h => ({
          id: h.id, // IMPORTANT: Preserve document ID for delete functionality
          address: h.address,
          apartmentNumber: h.apartmentNumber,
          city: h.city,
          zipCode: h.zipCode,
          housingCooperative: h.housingCooperative
        }));
        setMyApartments(convertedApartments);
      }
    });

    return () => unsubscribe();
  }, []);

  // Kontrollera sessionStorage för pending PersonData efter att komponenten har laddat
  useEffect(() => {
    const checkPendingPersonData = async () => {

      const pendingPersonData = sessionStorage.getItem('pendingPersonData');

      
      if (pendingPersonData) {
        try {
          const data = JSON.parse(pendingPersonData);

          
          // Kontrollera först om användaren redan har en verifierad adress
          if (auth.currentUser?.uid) {

            const { checkUserHasVerifiedAddress } = await import('../../api/housing');
            
            // Försök extrahera personnummer från PersonData för backup-sökning
            const personalNumber = userInfo?.personalNumber || data.personalNumber || null;

            
            const verifiedAddress = await checkUserHasVerifiedAddress(auth.currentUser.uid, personalNumber);

            
            if (verifiedAddress.hasVerifiedAddress) {

              // Rensa sessionStorage - vi behöver inte fråga igen
              sessionStorage.removeItem('pendingPersonData');

              return;
            }
          }
          
          // Om vi har adressdata och ingen verifierad adress, visa bekräftelse pop-up
          if (data.Basic?.Street) {

            setAddressForm({
              street: data.Basic.Street || '',
              zipCode: data.Basic.ZipCode || '',
              city: data.Basic.City || '',
              apartmentNumber: data.Basic.ApartmentNumber || '',
              entrance: data.Basic.Entrance || ''
            });
            
            // Spara PersonData i komponentens state för senare användning
            setPendingPersonData(data);

            
            setShowAddressModal(true);
            
            // Rensa sessionStorage efter att vi har sparat i state
            sessionStorage.removeItem('pendingPersonData');

          } else {

          }
        } catch (error) {
          sessionStorage.removeItem('pendingPersonData');
        }
      } else {

      }
    };

    // Kontrollera efter en kort fördröjning för att säkerställa att komponenten är redo
    const timer = setTimeout(checkPendingPersonData, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Enkel email-validering - bara kolla att @ finns med
  const isValidEmail = (email) => {
    return email && email.includes('@');
  };

  // Kolla om alla fält är ifyllda
  const allFieldsFilled = () => {
    return profileValues.firstName.trim() !== '' &&
           profileValues.lastName.trim() !== '' &&
           profileValues.email.trim() !== '' &&
           profileValues.phoneNumber.trim() !== '';
  };

  // Kolla om email är giltig
  const emailIsValid = () => {
    return isValidEmail(profileValues.email);
  };

  // Knappen är aktiv om alla fält är ifyllda OCH email är giltig
  const canSave = () => {
    return allFieldsFilled() && emailIsValid() && !isLoading;
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    
    setProfileValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveProfile = async () => {
    // Enkel kontroll - om vi kommit hit så vet vi att allt är ifyllt och email är giltig
    const success = await saveUserInfo({
      email: profileValues.email,
      phoneNumber: profileValues.phoneNumber,
      firstName: profileValues.firstName,
      lastName: profileValues.lastName
    });

    if (success) {
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
    }
  };

  // Funktion för att hantera adress-formuläret
  const handleAddressChange = (field) => (event) => {
    setAddressForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  // Funktion för att spara adress
  const handleSaveAddress = async () => {
    try {

      
      // Använd PersonData från komponentens state
      if (!pendingPersonData) {
        return;
      }


      
      // Process PersonData och matcha mot housing
      const result = await processPersonDataAddress(pendingPersonData, userInfo);
      

      
      // Spara adressdata för framtida användning med BRF-namn om matchning finns
      await saveAddressData(pendingPersonData, userInfo, result.housingCooperative);
      
      // Lägg till lägenheten i local state
      const newApartment = {
        address: addressForm.street,
        apartmentNumber: addressForm.apartmentNumber,
        city: addressForm.city,
        zipCode: addressForm.zipCode,
        housingCooperative: result.housingCooperative || (result.type === 'MATCHED' ? result.data.housingCooperative : 'Väntar på verifiering')
      };
      
      setMyApartments(prev => [...prev, newApartment]);
      
      // Visa success message
      setShowAddressModal(false);
      setPendingPersonData(null); // Rensa PersonData från state
      setSuccessMessage(result.message);
      setShowSaveSuccess(true);
      
      // Visa specifik message baserat på resultat
      if (result.type === 'MATCHED') {

      } else {

      }
      
      setTimeout(() => setShowSaveSuccess(false), 5000);
      
    } catch (error) {
      // Visa error message
      setShowAddressModal(false);
      setPendingPersonData(null); // Rensa PersonData även vid fel
      alert("Fel vid sparande av adress: " + error.message);
    }
  };

  // Funktion för att avbryta adress-redigering
  const handleCancelAddress = () => {
    setShowAddressModal(false);
    setPendingPersonData(null); // Rensa PersonData när användaren avbryter

  };

  const handleAddApartment = (newApartment) => {
    // For now, just add to local state - later we'll save to database
    setMyApartments(prev => [...prev, newApartment]);
    
    // Show success message
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };

  const handleRemoveApartment = (index) => {
    const apartment = myApartments[index];
    setApartmentToDelete({ ...apartment, index });
    setShowDeleteModal(true);
  };

  const confirmDeleteApartment = async () => {
    try {
      if (!apartmentToDelete || !apartmentToDelete.id) {
        console.error('No document ID found for apartment');
        setSuccessMessage('Fel: Kunde inte hitta lägenhetens ID');
        setShowSaveSuccess(true);
        setTimeout(() => setShowSaveSuccess(false), 3000);
        return;
      }

      // Ta bort ägarskap från Firestore (lägenheten finns kvar i systemet)
      const result = await deleteHousing(apartmentToDelete.id);

      if (result.success) {
        // Ta bort från lokal state
        setMyApartments(prev => prev.filter((_, i) => i !== apartmentToDelete.index));

        // Visa success message
        setSuccessMessage('Lägenheten har tagits bort från din profil');
        setShowSaveSuccess(true);
        setTimeout(() => setShowSaveSuccess(false), 3000);

        // Stäng modal och nollställ
        setShowDeleteModal(false);
        setApartmentToDelete(null);
      } else {
        console.error('Failed to delete apartment:', result.error);
        setSuccessMessage('Fel: Kunde inte ta bort kopplingen till lägenheten');
        setShowSaveSuccess(true);
        setTimeout(() => setShowSaveSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Error deleting apartment:', error);
      setSuccessMessage('Fel: Ett oväntat fel uppstod');
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#62B8E9]"></div>
      </div>
    );
  }

  return (
    <Box className="max-w-7xl mx-auto p-4 md:p-8 bg-gray-50 min-h-screen" style={{ fontFamily: 'Poppins, sans-serif' }}>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-2 text-[#2C2C2C]">Min profil</h1>
        <p className="text-[#6C6C6C]">Hantera dina personuppgifter och lägenheter</p>
      </div>

      {/* Success Message */}
      {showSaveSuccess && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center gap-3">
          <Check className="text-green-600" />
          <span className="text-green-800 font-medium">
            {successMessage || 'Ändringarna har sparats!'}
          </span>
        </div>
      )}

      {/* Grid Layout for Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Personuppgifter Card */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="rounded-full bg-[#e6f3fa] p-3">
              <Person className="text-[#62B8E9]" />
            </div>
            <h2 className="text-xl font-bold text-[#2C2C2C]">Personuppgifter</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                Förnamn <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={profileValues.firstName}
                onChange={handleInputChange('firstName')}
                placeholder="Ditt förnamn"
                className="w-full border border-[#E8E8E8] rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#62B8E9]/20 focus:border-[#62B8E9] hover:border-[#62B8E9] transition"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                Efternamn <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={profileValues.lastName}
                onChange={handleInputChange('lastName')}
                placeholder="Ditt efternamn"
                className="w-full border border-[#E8E8E8] rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#62B8E9]/20 focus:border-[#62B8E9] hover:border-[#62B8E9] transition"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                E-postadress <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={profileValues.email}
                onChange={handleInputChange('email')}
                placeholder="din.email@exempel.se"
                className="w-full border border-[#E8E8E8] rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#62B8E9]/20 focus:border-[#62B8E9] hover:border-[#62B8E9] transition"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                Telefonnummer <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={profileValues.phoneNumber}
                onChange={handleInputChange('phoneNumber')}
                placeholder="070-123 45 67"
                className="w-full border border-[#E8E8E8] rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#62B8E9]/20 focus:border-[#62B8E9] hover:border-[#62B8E9] transition"
              />
            </div>
            {profileValues.personalNumber && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Personnummer
                </label>
                <input
                  type="text"
                  value={profileValues.personalNumber}
                  disabled
                  className="w-full border border-[#E8E8E8] rounded-xl px-3 py-2 bg-[#E8E8E8] text-[#6C6C6C] cursor-not-allowed"
                />
              </div>
            )}
          </div>

          {/* Terms and Privacy Policy Text */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-sm text-[#6C6C6C] text-center">
              Genom att spara dina uppgifter accepterar du våra{' '}
              <a 
                href="/Hyria-allmanna%20villkor.docx.pdf"
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#62B8E9] hover:text-[#4A9FD1] underline font-medium transition-colors"
              >
                användarvillkor
              </a>
              {' '}samt{' '}
              <a 
                href="/HyriaPersonuppgiftspolicy.docx.pdf"
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#62B8E9] hover:text-[#4A9FD1] underline font-medium transition-colors"
              >
                integritetspolicy
              </a>
              .
            </p>
          </div>

          <div className="flex justify-end mt-6">
            <button
              onClick={handleSaveProfile}
              disabled={!canSave()}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition ${
                canSave()
                  ? 'bg-[#62B8E9] text-white hover:bg-[#4A9FD1] shadow-md'
                  : 'bg-[#E8E8E8] text-[#6C6C6C] cursor-not-allowed'
              }`}
            >
              <Save sx={{ fontSize: 18 }} />
              {isLoading ? 'Sparar...' : 'Spara ändringar'}
            </button>
          </div>
        </div>

        {/* Mina Lägenheter Card */}
        <div className="bg-white rounded-2xl shadow p-6">
          <MyApartments
            apartments={myApartments}
            onAddApartment={handleAddApartment}
            onRemoveApartment={handleRemoveApartment}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Adress-bekräftelse Modal */}
      {showAddressModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="rounded-full bg-blue-100 p-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Bekräfta din adress
              </h3>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              Vi har hittat följande adress från SPAR. Stämmer denna adress? Om inte, redigera den nedan.
            </p>
            
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Gatuadress
                </label>
                <input
                  type="text"
                  value={addressForm.street}
                  onChange={handleAddressChange('street')}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                  placeholder="Gatuadress"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Postnummer
                  </label>
                  <input
                    type="text"
                    value={addressForm.zipCode}
                    onChange={handleAddressChange('zipCode')}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                    placeholder="12345"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Ort
                  </label>
                  <input
                    type="text"
                    value={addressForm.city}
                    onChange={handleAddressChange('city')}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                    placeholder="Stockholm"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Lägenhetsnummer
                  </label>
                  <input
                    type="text"
                    value={addressForm.apartmentNumber}
                    onChange={handleAddressChange('apartmentNumber')}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                    placeholder="1001"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Entré
                  </label>
                  <input
                    type="text"
                    value={addressForm.entrance}
                    onChange={handleAddressChange('entrance')}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                    placeholder="A"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleCancelAddress}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleSaveAddress}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                Spara adress
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Apartment Modal */}
      <DeleteApartmentModal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setApartmentToDelete(null);
        }}
        apartment={apartmentToDelete}
        onConfirm={confirmDeleteApartment}
      />
    </Box>
  );
};

export default HomeV3; 